import { useEffect, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Icon } from './Icon';

type AppShellProps = {
  sidebar: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
};

/**
 * The two-column layout from MAFA: a 264 px sidebar and a flex main column
 * made of (topbar + scrollable content).
 *
 * On narrow viewports (≤ 900px, see global.css) the sidebar collapses into
 * an off-canvas drawer toggled by the burger button rendered in the topbar
 * frame. The drawer closes on route change and on Escape so navigation feels
 * native on mobile.
 */
export function AppShell({ sidebar, topbar, children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the drawer whenever the route changes (i.e. a nav link was tapped).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className={`shell${drawerOpen ? ' drawer-open' : ''}`}>
      <button
        type="button"
        className="shell-scrim"
        aria-label="Fermer le menu"
        tabIndex={drawerOpen ? 0 : -1}
        onClick={() => setDrawerOpen(false)}
      />
      {sidebar}
      <main className="main">
        <div className="topbar-frame">
          <button
            type="button"
            className="burger"
            aria-label="Ouvrir le menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <Icon name="menu" />
          </button>
          {topbar}
        </div>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
